import {QuantityRange} from '@hconnect/tuc-processes/fields'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import {Box, Typography} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {RangeEditableField} from './components/EditableField'
import {IconButton} from './components/IconButton'
import {QuantityDisplayValue} from './components/QuantityDisplayValue'
import {ValueContainer} from './components/ValueContainer'
import {useQuantityInput} from './hooks/useQuantityInput'

/**
 * Props for the QuantityInput component.
 */
export interface QuantityInputProps {
  /** Current quantity value */
  value: number | null

  /** Callback fired when quantity value changes */
  onChange: (value: number) => void

  /** Range configuration including min, max, step, default, and unit of measure */
  range: QuantityRange

  /** Optional callback fired when auto-correction occurs */
  onAutoCorrect?: (correction: {
    originalValue: number
    correctedValue: number
    reason: 'step' | 'min' | 'max'
  }) => void

  /** Whether the input is disabled */
  disabled?: boolean

  /**
   * Visual state of the input (similar to ControlledInput state).
   * - 'error': Shows error styling (red border)
   * - 'warning': Shows warning styling (yellow/orange border)
   * - undefined: Shows default styling
   */
  state?: 'error' | 'warning'

  /**
   * Optional hint text to display below the input.
   * Can be used for additional guidance or information.
   */
  hintText?: string

  /**
   * Optional custom label for the input field.
   * If not provided, defaults to 'Quantity'.
   */
  label?: string

  /** Test ID for the component */
  'data-test-id'?: string
}

/**
 * Determine border color based on state.
 */
const getBorderColor = (state: QuantityInputProps['state']) => {
  if (state === 'error') return '#d32f2f'
  if (state === 'warning') return '#ed6c02'
  return '#d1d9e0'
}

/**
 * Determine hint text color based on state.
 */
const getHintColor = (state: QuantityInputProps['state']) => {
  if (state === 'error') return '#d32f2f'
  if (state === 'warning') return '#ed6c02'
  return '#5f6368'
}

/**
 * Custom numeric input component with increment/decrement controls
 * and unit of measure display, designed to match Figma specifications.
 */
export const QuantityInput: React.FC<QuantityInputProps> = ({
  value,
  onChange,
  range,
  onAutoCorrect,
  disabled,
  state,
  hintText,
  label,
  'data-test-id': dataTestId = 'quantity-input'
}) => {
  const {t} = useTranslation()

  const {
    isEditing,
    incrementButtonProps,
    decrementButtonProps,
    displayValueProps,
    editableFieldProps
  } = useQuantityInput({
    value,
    onChange,
    range,
    onAutoCorrect,
    disabled
  })

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}
      data-test-id={dataTestId}
    >
      <Box
        sx={{
          width: '100%',
          height: '48px',
          position: 'relative'
        }}
      >
        {/* Field Container */}
        <Box
          sx={{
            backgroundColor: '#ffffff',
            height: '48px',
            borderRadius: '4px',
            border: `1px solid ${getBorderColor(state)}`,
            opacity: disabled ? 0.5 : 1,
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
              height: '48px',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: '4px',
              width: '100%'
            }}
          >
            {/* Decrement Button */}
            <IconButton
              icon={<RemoveIcon sx={{fontSize: '16px'}} />}
              {...decrementButtonProps}
              data-test-id={`${dataTestId}-decrement`}
            />

            {/* Label + Value Container */}
            <ValueContainer
              label={label ?? t('orderIntake.rmc.steps.quantity.quantityLabel', 'Quantity')}
            >
              {isEditing ? (
                <RangeEditableField {...editableFieldProps} data-test-id={`${dataTestId}-field`} />
              ) : (
                <QuantityDisplayValue
                  {...displayValueProps}
                  unit={range.uom}
                  data-test-id={`${dataTestId}-display`}
                />
              )}
            </ValueContainer>

            {/* Increment Button */}
            <IconButton
              icon={<AddIcon sx={{fontSize: '16px'}} />}
              {...incrementButtonProps}
              data-test-id={`${dataTestId}-increment`}
            />
          </Box>
        </Box>
      </Box>

      {/* Hint Text */}
      {hintText && (
        <Typography
          variant="body2"
          sx={{
            fontSize: '12px',
            lineHeight: '16px',
            color: getHintColor(state),
            marginTop: '4px'
          }}
          data-test-id={`${dataTestId}-hint`}
        >
          {hintText}
        </Typography>
      )}
    </Box>
  )
}
